<template>
  <div>
    <woot-tabs :index="selectedTabIndex" @change="onClickTabChange">
      <woot-tabs-item
        v-for="tab in tabs"
        :key="tab.key"
        :name="tab.name"
        :show-badge="false"        
      />
    </woot-tabs>
    <div class="bar-chart-scroll margin-top-1">
      <div class="bar-chart-container">      
        <canvas ref="canvas"></canvas>
        <div class="bar-chart-loading" v-if="loading">
          <spinner :colorScheme="'dark'" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import Spinner from 'shared/components/Spinner.vue';

import { format } from 'date-fns';

export default {
  extends: Bar,
  name: 'WhatsappEventsCategoryChart',
  components: {    
    Spinner
  },
  props: {
    events: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      selectedTabIndex: 0,
      tabs: [
        {
          key: 0,
          name: this.$t('WHATSAPP_CONSUMPTIONS_REPORT.TAB_EVENT_CATEGORY.TEMPLATE_CONVERSATION_SERVICE'),
        },
        {
          key: 1,
          name: this.$t('WHATSAPP_CONSUMPTIONS_REPORT.TAB_EVENT_CATEGORY.MESSAGES_SERVICE'),
        },
      ]
    }
  },
  methods: {
    updateChart() {
      let categoryTypes = [];
      if(this.selectedTabIndex == 0){
        categoryTypes = [
          "marketing_total",
          "utility_total",         
          "authentication_total",
          "service_total"
        ];
      }else{
        categoryTypes = [    
          "messages_service_total"
        ];
      }
      const colorMap = {
        marketing_total: '#9575cd',
        service_total: '#42a5f5',
        utility_total: '#ffb74d',
        authentication_total: '#4db6ac',
        messages_service_total: "#2563eb"
      };

      const grouped = {};
      const data = JSON.parse(JSON.stringify(this.events));
      data.sort((a, b) => new Date(a.date) - new Date(b.date));
      data.forEach(item => {
        const date = format(new Date(item.date), 'dd/MM/yyyy');
        if (!grouped[date]) {
          grouped[date] = {};
        }
        categoryTypes.forEach(category => {
          if(!grouped[date][category]){
            grouped[date][category] = 0;
          }
          grouped[date][category] += (item[category] || 0);
        });        
        
      });
    
      const labels = Object.keys(grouped).filter(date => {
        const categories = grouped[date];
        const total = categoryTypes.reduce(
          (sum, category) => sum + (categories[category] || 0),
          0
        );
        return total > 0;
      });

      const datasets = categoryTypes.map(category => ({
        label: this.categoryLabel(category),
        backgroundColor: colorMap[category],
        data: labels.map(date => grouped[date]?.[category] || 0),
        stack: 'categories',
      }));

      const hasData = datasets.some(ds => ds.data.some(val => val > 0));
      const finalLabels = hasData
        ? labels
        : [];
      const finalDatasets = hasData
        ? datasets
        : [];

      this.renderChart(
        {
          labels: finalLabels,
          datasets: finalDatasets,
        },
        {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                stacked: true,
                ticks: { fontSize: 12 },
              },
            ],
            yAxes: [
              {
                stacked: true,
                ticks: {
                  beginAtZero: true,
                  fontSize: 12,
                  callback: value =>
                    value >= 1000 ? `${value / 1000} mil` : value,
                },
              },
            ],
          },
          legend: {
            labels: { fontSize: 12 },
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const dataset = data.datasets[tooltipItem.datasetIndex];
                const label = dataset.label || '';
                const value = tooltipItem.value || 0;
                if (Number(value) === 0) return '';
                return `${label}: ${Number(value).toLocaleString('pt-BR')}`;
              },
            },
          },
        }
      );
    },

    categoryLabel(category) {
      switch (category) {
        case 'marketing_total':
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.TEMPLATES_MARKETING');
        case 'service_total':
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.CONVERSATONS_SERVICE');
        case 'utility_total':
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.TEMPLATES_UTILITY');
        case 'authentication_total':
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.TEMPLATES_AUTHENTICATION');
        case 'messages_service_total':
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.MESSAGES_SERVICE');
        default:
          return this.$t('WHATSAPP_CONSUMPTIONS_REPORT.CATEGORY.UNKNOWN');
      }
    },
    onClickTabChange(index) {
      if(index == this.selectedTabIndex){
        return;
      }
      this.selectedTabIndex = index;
      this.updateChart();
    },
  },

  watch: {
    events: {
      handler() {
        this.updateChart();
      },
    },
  },

  mounted() {
    this.updateChart();
  },
};
</script>

<style lang="scss" scoped>
.bar-chart-scroll {
  overflow-x: auto;
  width: 100%;
}

.bar-chart-container {
  position: relative;
  height: 300px;
  width: 100%;
  min-width: 600px;
  .bar-chart-loading{
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: #FFF;
  }
}
</style>
<style lang="scss">
  .bar-chart-container .bar-chart-loading .spinner{
    margin: auto;

  }
</style>
